import React from 'react';

/**
 * UAT chrome for dual-model paragraph comparison on Adult ADHD review pages.
 *
 * Three mutually exclusive views after drafts exist:
 *   - Gemini: stored draft in the document (editable, source of truth)
 *   - Claude: fallback draft overlaid in the document (read-only)
 *   - Side by side: Gemini stays in the document; Claude replaces the
 *     AI-inputs panel so both paragraphs can be read together
 *
 * The side-by-side control is hidden until Claude drafts have been
 * generated — there is nothing to put in the right column otherwise.
 *
 * This strip is a temporary non-production UAT control. Styling matches
 * the existing compare chrome rather than the clinical design system.
 */
export function CompareModeToolbar({
  isLoading,
  hasComparison,
  showClaude,
  sideBySide,
  progress,
  onGenerate,
  onShowGemini,
  onShowClaude,
  onToggleSideBySide,
  loadingLabel,
}) {
  const geminiSelected = hasComparison && !showClaude && !sideBySide;
  const claudeSelected = hasComparison && showClaude && !sideBySide;

  return (
    <div className="flex items-center gap-3 px-6 py-2 border-b border-gray-200 bg-gray-50">
      {!isLoading && !hasComparison && (
        <button
          type="button"
          onClick={onGenerate}
          className="px-3 py-1.5 text-sm font-medium text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors"
        >
          Compare with Claude
        </button>
      )}

      {isLoading && (
        <span className="text-sm text-gray-500">
          {loadingLabel || (
            progress?.total
              ? `Generating Claude outputs... (${progress.completed}/${progress.total} sections)`
              : 'Generating Claude outputs...'
          )}
        </span>
      )}

      {!isLoading && hasComparison && (
        <>
          <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg p-0.5">
            <button
              type="button"
              onClick={onShowGemini}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                geminiSelected
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Gemini
            </button>
            <button
              type="button"
              onClick={onShowClaude}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                claudeSelected
                  ? 'bg-purple-100 text-purple-800'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Claude
            </button>
          </div>

          <button
            type="button"
            onClick={onToggleSideBySide}
            aria-pressed={sideBySide}
            className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${
              sideBySide
                ? 'bg-violet-100 text-violet-800 border-violet-300'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Side by side
          </button>
        </>
      )}

      {hasComparison && sideBySide && (
        <span className="text-xs text-violet-700 font-medium">
          Gemini on the left, Claude on the right. Approvals stay on Gemini.
        </span>
      )}
      {hasComparison && showClaude && !sideBySide && (
        <span className="text-xs text-purple-600 font-medium">
          Viewing Claude outputs (read-only)
        </span>
      )}
    </div>
  );
}
